import { canonicalUserId } from '../../utils/canonicalUserId';
/**
 * Maps tracks to user sessions and stores optimal delays.
 *
 * - Maps trackId -> { userSessionId, type }
 * - Stores optimal delays per video track
 * - Provides method to get max optimal delay for a session by any track ID
 */
export class OptimalDelayMapper {
    // trackId -> TrackInfo
    tracks = new Map();
    userIdFromSessionId(userSessionId) {
        const colonIdx = userSessionId.indexOf(':');
        return colonIdx >= 0
            ? userSessionId.slice(0, colonIdx)
            : userSessionId;
    }
    // trackId -> optimalDelay (only for video tracks)
    trackOptimalDelays = new Map();
    /**
     * Register a track with its userSessionId and type.
     */
    registerTrackIfNotExists(trackId, userSessionId, type) {
        if (this.tracks.has(trackId)) {
            return;
        }
        this.tracks.set(trackId, { userSessionId, type });
    }
    /**
     * Update the optimal delay for a video track.
     */
    updateOptimalDelay(trackId, optimalDelay) {
        if (optimalDelay !== undefined) {
            this.trackOptimalDelays.set(trackId, optimalDelay);
        }
    }
    /**
     * Get the maximum optimal delay for a session.
     * Looks up the userSessionId from the given trackId,
     * then returns the max optimal delay among all VIDEO tracks in that session.
     */
    getOptimalDelayForSession(trackId) {
        const trackInfo = this.tracks.get(trackId);
        if (!trackInfo) {
            return undefined;
        }
        let maxOptimalDelay;
        // Find max optimal delay among all tracks (video and audio) in the same session
        for (const [tid, info] of this.tracks.entries()) {
            if (info.userSessionId === trackInfo.userSessionId) {
                const optimalDelay = this.trackOptimalDelays.get(tid);
                if (optimalDelay !== undefined) {
                    if (maxOptimalDelay === undefined ||
                        optimalDelay > maxOptimalDelay) {
                        maxOptimalDelay = optimalDelay;
                    }
                }
            }
        }
        return maxOptimalDelay;
    }
    /**
     * Remove a track.
     */
    removeTrack(trackId) {
        this.tracks.delete(trackId);
        this.trackOptimalDelays.delete(trackId);
    }
    /**
     * Get all track IDs belonging to a user session.
     */
    getTrackIdsForSession(userSessionId) {
        const trackIds = [];
        for (const [trackId, info] of this.tracks.entries()) {
            if (info.userSessionId === userSessionId) {
                trackIds.push(trackId);
            }
        }
        return trackIds;
    }
    /**
     * Remove all tracks for a user session.
     */
    removeSession(userSessionId) {
        for (const [trackId, info] of this.tracks.entries()) {
            if (info.userSessionId === userSessionId) {
                this.tracks.delete(trackId);
                this.trackOptimalDelays.delete(trackId);
            }
        }
    }
    /**
     * Clear all mappings.
     */
    clear() {
        this.tracks.clear();
        this.trackOptimalDelays.clear();
    }
    /**
     * Get the userSessionId for a track.
     */
    getUserSessionIdForTrack(trackId) {
        return this.tracks.get(trackId)?.userSessionId;
    }
    /**
     * Get the type of a track.
     */
    getTrackType(trackId) {
        return this.tracks.get(trackId)?.type;
    }
    /**
     * Get the userId for a track (extracted from the userSessionId).
     */
    getUserIdForTrack(trackId) {
        const info = this.tracks.get(trackId);
        if (!info) {
            return undefined;
        }
        return this.userIdFromSessionId(info.userSessionId);
    }
    /**
     * Get video track IDs for a userId (matches userSessionIds that start with `userId:`).
     */
    getVideoTrackIdsForUser(userId) {
        const want = canonicalUserId(userId);
        const result = [];
        for (const [trackId, info] of this.tracks.entries()) {
            if (info.type !== 'video')
                continue;
            const sidUser = this.userIdFromSessionId(info.userSessionId);
            if (canonicalUserId(sidUser) === want) {
                result.push(trackId);
            }
        }
        return result;
    }
    /**
     * Get all video track IDs in the same session as the given track.
     */
    getVideoTrackIdsInSameSession(trackId) {
        const trackInfo = this.tracks.get(trackId);
        if (!trackInfo) {
            return [];
        }
        const videoTrackIds = [];
        for (const [tid, info] of this.tracks.entries()) {
            if (info.userSessionId === trackInfo.userSessionId &&
                info.type === 'video') {
                videoTrackIds.push(tid);
            }
        }
        return videoTrackIds;
    }
    getActiveVideoTracks() {
        const result = [];
        for (const [trackId, info] of this.tracks.entries()) {
            if (info.type !== 'video')
                continue;
            const rawUserId = this.userIdFromSessionId(info.userSessionId);
            result.push({ trackId, userId: canonicalUserId(rawUserId) });
        }
        return result;
    }
    getActiveAudioTracks() {
        const result = [];
        for (const [trackId, info] of this.tracks.entries()) {
            if (info.type !== 'audio')
                continue;
            const rawUserId = this.userIdFromSessionId(info.userSessionId);
            result.push({ trackId, userId: canonicalUserId(rawUserId) });
        }
        return result;
    }
    /**
     * Get all unique UserSessionIds for a userId (matches userSessionIds that start with `userId:`).
     */
    getSessionsForUser(userId) {
        const want = canonicalUserId(userId);
        const sessions = new Set();
        for (const info of this.tracks.values()) {
            const sidUser = this.userIdFromSessionId(info.userSessionId);
            if (canonicalUserId(sidUser) === want) {
                sessions.add(info.userSessionId);
            }
        }
        return Array.from(sessions);
    }
    getAudioTrackIdsForUser(userId) {
        const want = canonicalUserId(userId);
        const result = [];
        for (const [trackId, info] of this.tracks.entries()) {
            if (info.type !== 'audio')
                continue;
            const sidUser = this.userIdFromSessionId(info.userSessionId);
            if (canonicalUserId(sidUser) === want) {
                result.push(trackId);
            }
        }
        return result;
    }
}
